<script>
export default {
  data() {
    return {
      userData: JSON.parse(localStorage.getItem("user")) || {},
    };
  },
  methods: {
    toggleMenu() {
      this.$parent.toggleMenu();
    },
  },
};
</script>

<template>
  <header class="app-header">
    <div class="header-container">
      <!-- Left Section -->
      <div class="header-left">
        <!-- Logo and Menu Toggle -->
        <div class="brand-container">
          <button class="menu-toggle" @click="toggleMenu">
            <i class="mdi mdi-menu"></i>
          </button>
          <router-link to="/" class="app-logo">
            <img src="@/assets/images/logo.png" alt="Logo" class="logo-img">
          </router-link>
        </div>

        <!-- Business Info -->
        <div class="business-info" v-if="userData.business">
          <div class="business-name">
            <span class="business-title">{{ userData.business }}</span>
            <div class="business-details">
              <span class="business-location">
                <i class="mdi mdi-map-marker-outline"></i>
                {{ userData.location }}
              </span>
              <span class="business-contact">
                <i class="mdi mdi-phone-outline"></i>
                {{ userData.contact }}
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Section -->
      <div class="header-right">
        <!-- User Dropdown -->
        <div class="user-dropdown">
          <b-dropdown 
            right 
            variant="link" 
            toggle-class="user-dropdown-toggle"
            menu-class="user-dropdown-menu"
            no-caret
          >
            <template v-slot:button-content>
              <div class="user-avatar">
                <i class="mdi mdi-account-circle"></i>
              </div>
              <div class="user-info">
                <span class="username">{{ userData.username || 'Account' }}</span>
              </div>
              <i class="mdi mdi-chevron-down dropdown-arrow"></i>
            </template>
            
            <b-dropdown-item href="/logout" class="logout-item">
              <i class="mdi mdi-logout"></i>
              <span>Logout</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
    </div>
  </header>
</template>

<style scoped>
/* Color Variables */
:root {
  --header-bg: #1a1a2e;
  --header-text: #ffffff;
  --primary-accent: #4f46e5;
  --secondary-accent: #6366f1;
  --hover-bg: rgba(255, 255, 255, 0.1);
  --transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Base Header Styles */
.app-header {
  background-color: var(--header-bg);
  color: var(--header-text);
  height: 64px;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
}

.header-container {
  width: 100%;
  padding: 0 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Left Section */
.header-left {
  display: flex;
  align-items: center;
  gap: 24px;
}

.brand-container {
  display: flex;
  align-items: center;
  gap: 12px;
}

.menu-toggle {
  background: transparent;
  border: none;
  color: var(--header-text);
  font-size: 24px;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  transition: var(--transition);
  display: flex;
  align-items: center;
  justify-content: center;
}

.menu-toggle:hover {
  background: var(--hover-bg);
}

.app-logo {
  display: flex;
  align-items: center;
}

.logo-img {
  height: 32px;
  transition: var(--transition);
}

/* Business Info */
.business-info {
  display: flex;
  align-items: center;
}

.business-name {
  display: flex;
  flex-direction: column;
}

.business-title {
  font-size: 16px;
  font-weight: 600;
  line-height: 1.5;
}

.business-details {
  display: flex;
  gap: 16px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
}

.business-location,
.business-contact {
  display: flex;
  align-items: center;
  gap: 4px;
}

/* Right Section */
.header-right {
  display: flex;
  align-items: center;
  gap: 16px;
}

/* User Dropdown */
.user-dropdown {
  position: relative;
}

.user-dropdown-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  transition: var(--transition);
  text-decoration: none;
  color: var(--header-text);
  background: transparent;
  border: none;
}

.user-dropdown-toggle:hover {
  background: var(--hover-bg);
}

.user-avatar {
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
}

.user-avatar i {
  font-size: 24px;
}

.user-info {
  display: flex;
  flex-direction: column;
}

.username {
  font-size: 14px;
  font-weight: 500;
}

.dropdown-arrow {
  font-size: 18px;
  transition: var(--transition);
}

.user-dropdown-menu {
  border: none;
  border-radius: 8px;
  padding: 8px 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  margin-top: 8px;
  background: var(--header-bg);
}

.user-dropdown-menu .dropdown-item {
  padding: 8px 16px;
  font-size: 14px;
  color: var(--header-text);
  display: flex;
  align-items: center;
  gap: 8px;
  transition: var(--transition);
}

.user-dropdown-menu .dropdown-item:hover {
  background: var(--hover-bg);
  color: var(--header-text);
}

.logout-item {
  color: #ef4444 !important;
}

.logout-item:hover {
  color: #fff !important;
  background: rgba(239, 68, 68, 0.1) !important;
}

/* Responsive Adjustments */
@media (max-width: 992px) {
  .business-details {
    display: none;
  }
}

@media (max-width: 768px) {
  .header-container {
    padding: 0 16px;
  }
  
  .business-title {
    display: none;
  }
  
  .username {
    display: none;
  }
  
  .user-dropdown-toggle {
    padding: 8px;
  }
}
</style>